import { FC, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { pincodeApi } from 'src/api/pincode';
import { ResponseCodes } from 'src/enums/responseCodes';

interface AddServiceAreaProps {
  open: boolean;
  onClose: () => void;
  addServiceAreas: (data: string[]) => Promise<boolean>;
  existingServiceAreaIds?: string[];
}

export const AddServiceArea: FC<AddServiceAreaProps> = (props) => {
  const { open, onClose, addServiceAreas, existingServiceAreaIds } = props;
  const [isAvailable, setIsAvailable] = useState<string>('Loading');
  const [states, setStates] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string>('');
  const [districts, setDistricts] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [codes, setCodes] = useState([]);
  const [selectedcodes, setSelectedcodes] = useState([]);
  const [duplicateMessage, setDuplicateMessage] = useState<string>('');

  // Build a set of existing pincodes (string) to exclude from options
  const existingPincodeSet = useMemo(
    () => new Set((existingServiceAreaIds || []).map((id) => id.toString().split('#')[0])),
    [existingServiceAreaIds]
  );

  // For setting height of filtered data
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      setStates([]);
      setSelectedStates('');
      setDistricts([]);
      setSelectedDistricts([]);
      setCities([]);
      setSelectedCities([]);
      setCodes([]);
      setSelectedcodes([]);
      onClose();
    }
  };

  const handleAddServiceAreas = async () => {
    try {
      // Unique selected pincodes as strings
      const uniqueSelected = Array.from(new Set(selectedcodes.map((p) => p.toString())));
      // Split into new vs duplicates
      const newPincodes = uniqueSelected.filter((p) => !existingPincodeSet.has(p));
      const duplicatePincodes = uniqueSelected.filter((p) => existingPincodeSet.has(p));

      if (duplicatePincodes.length > 0) {
        setDuplicateMessage(`Already added: ${duplicatePincodes.join(', ')}. These were skipped.`);
      } else {
        setDuplicateMessage('');
      }

      if (newPincodes.length === 0) {
        // Nothing to add
        return;
      }

      const addResult = await addServiceAreas(newPincodes);
      if (addResult) {
        closeDialog('', '');
      }
    } catch (error) {
      console.error('Error adding service areas:', error);
    }
  };

  const filterCodes = (condition: string, itemsArray: string[] | Number[]) => {
    pincodeApi
      .getDistinctValues({ property: 'code', [condition]: itemsArray.toString() })
      .then((data) => {
        const values = (data?.responseData?.values || []) as (string | number)[];
        const filtered = values.filter((v) => !existingPincodeSet.has(v.toString()));
        setCodes(filtered);
      });
  };

  const getAllPincodes = async () => {
    pincodeApi
      .getDistinctValues({ property: 'code' })
      .then((data) => {
        const values = (data?.responseData?.values || []) as (string | number)[];
        const filtered = values.filter((v) => !existingPincodeSet.has(v.toString()));
        setCodes(filtered);
      });
  };

  const getStatesDetails = async () => {
    const stateResult = await pincodeApi.getDistinctValues({ property: 'state' });
    if (stateResult.statusCode === ResponseCodes.OK) {
      setStates(stateResult.responseData?.values);
    }
    setIsAvailable(stateResult.responseData?.values?.length > 0 ? 'Success' : 'Fail');
  };

  // handle selected state
  const handleStateChange = async (event): Promise<void> => {
    setSelectedDistricts([]);
    setSelectedCities([]);
    setSelectedcodes([]);
    setSelectedStates(event.target.value);
    if (event.target.value) {
      pincodeApi
        .getDistinctValues({ property: 'district', states: event.target.value.toString() })
        .then((data) => {
          setDistricts(data?.responseData?.values);
        });
      filterCodes('states', event.target.value);
    } else {
      setDistricts([]);
      getAllPincodes().catch(console.error);
    }
  };

  // handle selected district
  const handleDistChange = async (event): Promise<void> => {
    setSelectedCities([]);
    setSelectedcodes([]);
    setSelectedDistricts(event.target.value);
    pincodeApi
      .getDistinctValues({ property: 'city', districts: event.target.value.toString() })
      .then((data) => {
        setCities(data?.responseData?.values);
      });
    filterCodes('districts', event.target.value);
  };

  // handle selected city
  const handleCityChange = async (event): Promise<void> => {
    setSelectedcodes([]);
    setSelectedCities(event.target.value);
    filterCodes('cities', event.target.value);
  };

  // handle selected pincode
  const handlePincodesChange = (event): void => {
    setSelectedcodes(event.target.value);
  };

  useEffect(() => {
    getStatesDetails().catch(console.error);
    getAllPincodes().catch(console.error);
  }, []);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle sx={{ justifyContent: 'flex-end' }}>
        Add Service Areas
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isAvailable === 'Loading' && (
        <>
          <Typography
            sx={{
              ml: 4,
              mb: 4,
            }}
            variant="body2"
          >
            Loading...
          </Typography>
        </>
      )}
      {isAvailable === 'Fail' && (
        <Typography
          sx={{
            ml: 4,
            mb: 4,
          }}
          color="red"
          variant="body2"
        >
          Pincodes Are Not Available
        </Typography>
      )}
      {isAvailable === 'Success' && (
        <>
          <DialogContent
            sx={{
              mt: -2,
            }}
          >
            <Grid
              container
              spacing={1}
            >
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  State
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="state-select"
                    value={selectedStates}
                    onChange={(event) => {
                      handleStateChange(event);
                    }}
                    renderValue={(t) => t}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {states?.map((item) => (
                      <MenuItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  District
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="state-multi-select"
                    multiple
                    disabled={!selectedStates}
                    value={selectedDistricts}
                    onChange={(event) => {
                      handleDistChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {districts.map((district) => (
                      <MenuItem
                        key={district}
                        value={district}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedDistricts.indexOf(district) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={district} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Cities
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="state-multi-select"
                    multiple
                    disabled={!selectedStates || selectedDistricts.length === 0}
                    value={selectedCities}
                    onChange={(event) => {
                      handleCityChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {cities.map((city) => (
                      <MenuItem
                        key={city}
                        value={city}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedCities.indexOf(city) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={city} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Pincode
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl fullWidth>
                    <Select
                      labelId="pincode-multi-select"
                      multiple
                      value={selectedcodes}
                      onChange={(event) => handlePincodesChange(event)}
                      renderValue={(t) => t.join(', ')}
                      style={{
                        fontSize: '12px',
                        height: '35px',
                        width: '100%',
                      }}
                      MenuProps={MenuProps}
                    >
                      {codes?.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                        >
                          <ListItemIcon>
                            <Checkbox checked={selectedcodes.indexOf(option) > -1} />
                          </ListItemIcon>
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    color="primary"
                    onClick={() => {
                      setSelectedStates('');
                      setSelectedDistricts([]);
                      setSelectedCities([]);
                      setSelectedcodes([]);
                      getAllPincodes().catch(console.error);
                    }}
                    variant="outlined"
                    sx={{ minWidth: 'auto', height: '35px' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                minHeight: 250,
              }}
            >
              {duplicateMessage && (
                <Typography
                  color="red"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {duplicateMessage}
                </Typography>
              )}
              {selectedcodes.length > 0 && (
                <Box>
                  <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1 }}
                    variant="h6"
                  >
                    Selected Pincodes:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedcodes.map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              alignItems: 'right',
            }}
          >
            <Button
              color="primary"
              onClick={(e) => {
                closeDialog(e, '');
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={selectedcodes.length < 1}
              onClick={() => {
                handleAddServiceAreas();
              }}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AddServiceArea.defaultProps = {
  open: false,
  existingServiceAreaIds: [],
};

AddServiceArea.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  addServiceAreas: PropTypes.func,
  existingServiceAreaIds: PropTypes.array,
};
